import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Col,
  Dropdown,
  Empty,
  Input,
  MenuProps,
  Modal,
  Row,
  Select,
  Segmented,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDate } from '../utils/dateUtils';
import { getCatalogProductDimensionParts } from '../utils/productDimensions';
import { getProductVariantDisplayName, isRealProductVariant } from '../utils/productSelectorLabel';
import { Product } from '../types/pz';
import {
  deleteProduct,
  fetchProductCategories,
  fetchProducts as fetchProductsApi,
  permanentlyDeleteProduct
} from '../modules/products/api';
import {
  canDeleteProductCatalogItem,
  canManageProductCatalog,
} from '../modules/products/productCatalogAccessPolicy';
import { ProductCategory, fetchProductCategoryTags } from '../modules/products/productCategoriesApi';
import ProductCategoriesTab from '../components/products/ProductCategoriesTab';
import './Products.css';

const { Option } = Select;
const { Text, Title } = Typography;

interface ProductRecord extends Product {
  variantCount?: number | string;
}

interface ProductFilters {
  search: string;
  status: 'ACTIVE' | 'INACTIVE' | null;
  category: string | null;
  productCategoryId: string | null;
}

type ProductView = 'PRODUCTS' | 'HIERARCHIES';

interface ProductCategoryCellProps {
  name: string | null;
  color?: string | null;
  emptyLabel: React.ReactNode;
}

export const ProductCategoryCell: React.FC<ProductCategoryCellProps> = ({
  name,
  color,
  emptyLabel,
}) => {
  if (!name) return <Text type="secondary">{emptyLabel}</Text>;

  return (
    <Tooltip
      title={name}
      placement="topLeft"
      mouseEnterDelay={0.4}
      trigger={['hover', 'focus']}
    >
      <Tag
        className="products-category-tag"
        color={color || 'default'}
        tabIndex={0}
      >
        <span>{name}</span>
      </Tag>
    </Tooltip>
  );
};

const DEFAULT_PRODUCT_FILTERS: ProductFilters = {
  search: '',
  status: 'ACTIVE',
  category: null,
  productCategoryId: null,
};

const isProductActive = (product: ProductRecord) => product.isActive !== false;

export const getRealProductVariants = (product: ProductRecord) => (
  (product.variants || []).filter(isRealProductVariant)
);

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common']);
  const feedback = useFeedback();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'categories' ? 'categories' : 'list';

  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>(DEFAULT_PRODUCT_FILTERS);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [productView, setProductView] = useState<ProductView>('PRODUCTS');
  const [pendingProductAction, setPendingProductAction] = useState<{
    product: ProductRecord;
    action: 'DEACTIVATE' | 'PERMANENT_DELETE';
  } | null>(null);
  const [productActionLoading, setProductActionLoading] = useState(false);

  const handleTabChange = (key: string) => {
    setSearchParams(key === 'categories' ? { tab: 'categories' } : {});
  };

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageProductCatalog(currentUser);
  const canDelete = canDeleteProductCatalogItem(currentUser);

  const activeAdvancedFilterCount =
    (filters.status !== DEFAULT_PRODUCT_FILTERS.status ? 1 : 0) +
    (filters.category ? 1 : 0) +
    (filters.productCategoryId ? 1 : 0);
  const hasAnyFilter =
    filters.search !== DEFAULT_PRODUCT_FILTERS.search || activeAdvancedFilterCount > 0;

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.category, filters.productCategoryId]);

  useEffect(() => {
    fetchCategories();
    loadCategoryTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const productData = await fetchProductsApi(filters);
      setProducts(productData);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoryData = await fetchProductCategories();
      setCategories(categoryData);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchCategoriesFailed'));
    }
  };

  const loadCategoryTags = async () => {
    try {
      setCategoryTags(await fetchProductCategoryTags());
    } catch (error: any) {
      // Polish-first: show the translated message, not the backend English string.
      feedback.error(t('products:productCategory.loadFailed'));
    }
  };

  const handleProductCategoriesChanged = async () => {
    await Promise.all([loadCategoryTags(), fetchProducts()]);
  };

  const handleConfirmProductAction = async () => {
    if (!pendingProductAction) return;

    const productName = pendingProductAction.product.name;
    setProductActionLoading(true);
    try {
      if (pendingProductAction.action === 'PERMANENT_DELETE') {
        await permanentlyDeleteProduct(pendingProductAction.product.id);
        feedback.success(t('products:success.deleted', { name: productName }));
      } else {
        await deleteProduct(pendingProductAction.product.id);
        feedback.success(t('products:success.deactivated', { name: productName }));
      }
      setPendingProductAction(null);
      await fetchProducts();
    } catch (error: any) {
      if (error.response?.data?.code === 'PRODUCT_IN_USE') {
        feedback.error(t('products:error.productInUse', { name: productName }), { sticky: true });
      } else if (pendingProductAction.action === 'PERMANENT_DELETE') {
        feedback.error(t('products:error.deleteFailed', { name: productName }));
      } else {
        feedback.error(t('products:error.deactivateFailed', { name: productName }));
      }
    } finally {
      setProductActionLoading(false);
    }
  };

  const getStatusColor = (product: ProductRecord) =>
    isProductActive(product) ? 'success' : 'default';

  const getProductActionItems = (record: ProductRecord): MenuProps['items'] => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: t('common:view'),
    },
    ...(canEdit ? [{
      key: 'edit',
      icon: <EditOutlined />,
      label: t('common:edit'),
    }] : []),
    ...(canDelete ? [{
      key: isProductActive(record) ? 'deactivate' : 'permanent-delete',
      danger: true,
      icon: <DeleteOutlined />,
      label: isProductActive(record)
        ? t('products:actions.deactivate')
        : t('products:actions.permanentDelete'),
    }] : []),
  ];

  const handleProductAction = (key: string, record: ProductRecord) => {
    if (key === 'view') {
      navigate(`/app/products/${record.id}`);
      return;
    }

    if (key === 'edit') {
      navigate(`/app/products/${record.id}?mode=edit`);
      return;
    }

    if (key === 'deactivate' || key === 'permanent-delete') {
      setPendingProductAction({
        product: record,
        action: key === 'permanent-delete' ? 'PERMANENT_DELETE' : 'DEACTIVATE',
      });
    }
  };

  const columns: ColumnsType<ProductRecord> = [
    {
      title: t('products:fields.name'),
      dataIndex: 'name',
      key: 'name',
      width: 320,
      ellipsis: true,
      render: (name: string, record) => (
        <button className="products-name-button" onClick={() => navigate(`/app/products/${record.id}`)}>
          <span>{name}</span>
          {record.englishName && <small>{record.englishName}</small>}
        </button>
      ),
    },
    {
      title: t('products:fields.ean'),
      dataIndex: 'ean',
      key: 'ean',
      width: 160,
      ellipsis: true,
      render: (ean: string | null) => ean ? <Text copyable>{ean}</Text> : '-',
    },
    {
      title: t('products:fields.dimensions'),
      key: 'dimensions',
      width: 192,
      render: (_, record) => {
        const parts = getCatalogProductDimensionParts(record);
        if (parts.length === 0) return '-';

        return (
          <div className="products-dimensions">
            {parts.map((part) => (
              <span className="products-dimension" key={part.field}>
                <small>{part.label}</small>
                <strong>{part.value}</strong>
              </span>
            ))}
            <span className="products-dimensions-unit">mm</span>
          </div>
        );
      },
    },
    {
      title: t('products:fields.category'),
      dataIndex: 'productCategoryName',
      key: 'productCategoryName',
      width: 184,
      render: (name: string | null, record) => (
        <ProductCategoryCell
          name={name}
          color={record.productCategoryColor}
          emptyLabel={t('products:productCategory.none')}
        />
      ),
    },
    {
      title: t('products:fields.classification'),
      dataIndex: 'category',
      key: 'category',
      width: 184,
      render: (text: string) => text ? t(`products:categories.${text}`, { defaultValue: text }) : '-',
    },
    {
      title: t('products:fields.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 132,
      render: (_: boolean, record) => (
        <Tag color={getStatusColor(record)}>
          {t(`products:status.${isProductActive(record) ? 'active' : 'inactive'}`)}
        </Tag>
      ),
    },
    {
      title: t('products:fields.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 144,
      render: (date: string) => formatDate(date),
    },
    {
      title: t('common:actions'),
      key: 'actions',
      width: 88,
      align: 'center',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getProductActionItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              handleProductAction(key, record);
            },
          }}
          trigger={['click']}
        >
          <Button
            className="products-actions-button"
            icon={<MoreOutlined />}
            size="small"
            onClick={(event) => event.stopPropagation()}
            title={t('common:actions')}
          />
        </Dropdown>
      ),
    },
  ];

  const renderProductHierarchy = (product: ProductRecord) => {
    const variants = getRealProductVariants(product);

    return (
      <div className="products-hierarchy" aria-label={t('products:view.hierarchyFor', { name: product.name })}>
        {variants.map((variant) => (
          <div className="products-hierarchy-node products-hierarchy-node--variant" key={variant.id}>
            <span className="products-hierarchy-line" aria-hidden="true" />
            <div>
              <Text type="secondary">{t('products:view.variant')}</Text>
              <span className="products-hierarchy-variant-name">
                {getProductVariantDisplayName(variant, t('products:view.baseProduct'))}
              </span>
            </div>
            <Tag color={variant.isActive !== false ? 'success' : 'default'}>
              {t(`products:status.${variant.isActive !== false ? 'active' : 'inactive'}`)}
            </Tag>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="products-page">
      <div className="products-page-header">
        <div>
          <Title level={2}>{t('products:title')}</Title>
          <Text type="secondary">{t('products:subtitle')}</Text>
        </div>
        {canEdit && activeTab === 'list' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/app/products/new')}
          >
            {t('products:actions.create')}
          </Button>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'list',
            label: t('products:title'),
            children: (
              <>
      <div className="products-toolbar">
        <Input
          className="products-search"
          placeholder={t('products:filters.search')}
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(event) => setFilters({ ...filters, search: event.target.value })}
          allowClear
        />
        <Space wrap>
          <div className="products-view-control">
            <Text type="secondary">{t('products:view.label')}</Text>
            <Segmented
              aria-label={t('products:view.label')}
              value={productView}
              onChange={(value) => setProductView(value as ProductView)}
              options={[
                { label: t('products:view.products'), value: 'PRODUCTS' },
                { label: t('products:view.hierarchies'), value: 'HIERARCHIES' },
              ]}
            />
          </div>
          <Badge count={activeAdvancedFilterCount} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              type={advancedFiltersOpen || activeAdvancedFilterCount > 0 ? 'primary' : 'default'}
              ghost={advancedFiltersOpen || activeAdvancedFilterCount > 0}
              onClick={() => setAdvancedFiltersOpen((open) => !open)}
            >
              {t('products:filters.toggle')}
            </Button>
          </Badge>
          {hasAnyFilter && (
            <Button
              type="link"
              onClick={() => setFilters(DEFAULT_PRODUCT_FILTERS)}
            >
              {t('common:clearFilters')}
            </Button>
          )}
        </Space>
      </div>

      {advancedFiltersOpen && (
        <Row gutter={[12, 12]} className="products-filter-row">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              aria-label={t('products:filters.status')}
              placeholder={t('products:filters.status')}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%' }}
            >
              <Option value="ACTIVE">{t('products:status.active')}</Option>
              <Option value="INACTIVE">{t('products:status.inactive')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder={t('products:filters.classification')}
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value })}
              allowClear
              style={{ width: '100%' }}
            >
              {categories.map(category => (
                <Option key={category} value={category}>
                  {t(`products:categories.${category}`, { defaultValue: category })}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder={t('products:filters.category')}
              value={filters.productCategoryId}
              onChange={(value) => setFilters({ ...filters, productCategoryId: value ?? null })}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={categoryTags.map((tag) => ({ value: tag.id, label: tag.name }))}
            />
          </Col>
        </Row>
      )}

      <Table
        className="products-table"
        dataSource={products}
        columns={columns}
        loading={loading}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        expandable={productView === 'HIERARCHIES' ? {
          expandedRowKeys: products
            .filter((product) => getRealProductVariants(product).length > 0)
            .map((product) => product.id),
          expandedRowRender: renderProductHierarchy,
          rowExpandable: (product) => getRealProductVariants(product).length > 0,
          showExpandColumn: false,
        } : undefined}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('products:empty')}
            />
          ),
        }}
        scroll={{ x: 1500 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total, range) => t('common:pagination', { from: range[0], to: range[1], total }),
          pageSizeOptions: ['10', '25', '50', '100'],
          defaultPageSize: 25,
        }}
      />
              </>
            ),
          },
          {
            key: 'categories',
            label: t('products:productCategory.tab'),
            children: <ProductCategoriesTab canEdit={canEdit} onCategoriesChanged={handleProductCategoriesChanged} />,
          },
        ]}
      />
      <Modal
        title={pendingProductAction?.action === 'PERMANENT_DELETE'
          ? t('products:confirm.permanentDelete')
          : t('products:confirm.deactivate')}
        open={Boolean(pendingProductAction)}
        okText={pendingProductAction?.action === 'PERMANENT_DELETE'
          ? t('products:actions.permanentDelete')
          : t('products:actions.deactivate')}
        cancelText={t('common:no')}
        okButtonProps={{ danger: true }}
        confirmLoading={productActionLoading}
        onOk={handleConfirmProductAction}
        onCancel={() => {
          if (!productActionLoading) setPendingProductAction(null);
        }}
      />
    </div>
  );
};

export default Products;
